// src/components/Atelier3/Activite4/MeasureTable.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Edit2, Trash2, ChevronDown, ChevronUp, Eye, Trash, ArrowLeft, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../../api/apiClient';
import workshop3Service from '../../../services/workshop3Service'; // Use hydration service
import DeleteConfirmationModal from '../../common/DeleteConfirmationModal';

const CATEGORIES = {
  governance: { label: 'GOUVERNANCE', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  protection: { label: 'PROTECTION', color: 'bg-green-100 text-green-800 border-green-200' },
  defense: { label: 'DÉFENSE', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  resilience: { label: 'RÉSILIENCE', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  other: { label: 'AUTRE', color: 'bg-gray-100 text-gray-800 border-gray-200' }
};

const PRIORITIES = {
  high: { label: 'Haute', color: 'bg-red-100 text-red-800 border-red-200' },
  medium: { label: 'Moyenne', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  low: { label: 'Basse', color: 'bg-green-100 text-green-800 border-green-200' }
};

const IMPLEMENTATION_STATUSES = {
  planned: { label: 'Planifiée', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  'in-progress': { label: 'En cours', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  implemented: { label: 'Implémentée', color: 'bg-green-100 text-green-800 border-green-200' },
  abandoned: { label: 'Abandonnée', color: 'bg-red-100 text-red-800 border-red-200' }
};

const WORKSHOP_MEASURE_NAVIGATION_KEY = 'ebiosrm_measure_navigation_target';
const WORKSHOP_MEASURE_RETURN_KEY = 'ebiosrm_measure_return_context';
const WORKSHOP5_RESTORE_CONTEXT_KEY = 'ebiosrm_workshop5_restore_context';

const normalizeMeasureText = (value) =>
  (value || '').toString().trim().toLowerCase();

const flashHighlightElement = (element) => {
  if (!element || typeof window === 'undefined') return;

  const previousTransition = element.style.transition;
  const previousBackgroundColor = element.style.backgroundColor;
  const previousBoxShadow = element.style.boxShadow;

  element.style.transition = 'background-color 0.25s ease, box-shadow 0.25s ease';
  element.style.backgroundColor = '#FEF3C7';
  element.style.boxShadow = 'inset 0 0 0 2px rgba(245, 158, 11, 0.9)';

  window.setTimeout(() => {
    element.style.backgroundColor = previousBackgroundColor;
    element.style.boxShadow = previousBoxShadow;
    element.style.transition = previousTransition;
  }, 1800);
};

const MeasureTable = ({ measures, onEdit, onDelete, onBulkDelete, isLoading }) => {
  const { t } = useTranslation();
  const [expandedMeasure, setExpandedMeasure] = useState(null);
  const [sortField, setSortField] = useState('title');
  const [sortDirection, setSortDirection] = useState('asc');
  const [sourceRisks, setSourceRisks] = useState({});
  const [objectifsVises, setObjectifsVises] = useState({});
  const [attackPathsData, setAttackPathsData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [selectedMeasures, setSelectedMeasures] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [returnToWorkshop5, setReturnToWorkshop5] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    itemType: 'ecosystemMeasure',
    itemName: '',
    itemId: null,
    isBulk: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStakeholder, setFilterStakeholder] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterImplementation, setFilterImplementation] = useState('');
  const [filterResponsible, setFilterResponsible] = useState('');
  const [filterAttackPath, setFilterAttackPath] = useState('');
  const [stakeholderDirectory, setStakeholderDirectory] = useState([]);

  const toggleExpand = (measureId) => {
    if (expandedMeasure === measureId) {
      setExpandedMeasure(null);
    } else {
      setExpandedMeasure(measureId);
    }
  };

  // Handle individual measure selection
  const handleSelectMeasure = (measureId) => {
    const newSelected = new Set(selectedMeasures);
    if (newSelected.has(measureId)) {
      newSelected.delete(measureId);
    } else {
      newSelected.add(measureId);
    }
    setSelectedMeasures(newSelected);
    setSelectAll(filteredMeasures.length > 0 && newSelected.size === filteredMeasures.length);
  };

  // Handle select all toggle (operates on the filtered list)
  const handleSelectAll = () => {
    if (selectAll || filteredMeasures.length === 0) {
      setSelectedMeasures(new Set());
      setSelectAll(false);
    } else {
      setSelectedMeasures(new Set(filteredMeasures.map(m => m.id)));
      setSelectAll(true);
    }
  };

  // Handle bulk delete button click
  const handleBulkDeleteClick = () => {
    if (selectedMeasures.size === 0) return;
    
    setDeleteModal({
      isOpen: true,
      itemType: 'bulkMeasures',
      itemName: String(selectedMeasures.size),
      itemId: Array.from(selectedMeasures),
      isBulk: true
    });
  };

  // Confirm deletion (handled by modal)
  const confirmDelete = () => {
    if (deleteModal.isBulk) {
      onBulkDelete(deleteModal.itemId);
      setSelectedMeasures(new Set());
      setSelectAll(false);
    } else {
      onDelete(deleteModal.itemId);
    }
    setDeleteModal({ ...deleteModal, isOpen: false }); // Close modal after action
  };

  // Reset selection when measures change
  useEffect(() => {
    setSelectedMeasures(new Set());
    setSelectAll(false);
  }, [measures]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const rawContext = localStorage.getItem(WORKSHOP_MEASURE_RETURN_KEY);
    if (!rawContext) {
      return;
    }

    try {
      const parsedContext = JSON.parse(rawContext);
      if (parsedContext?.sourceTab === 'atelier5-synthese-scenarios' && parsedContext?.targetTab === 'atelier3-activite4') {
        setReturnToWorkshop5(parsedContext);
      }
    } catch (error) {
      console.warn('Failed to parse return-to-Workshop-5 context:', error);
    } finally {
      localStorage.removeItem(WORKSHOP_MEASURE_RETURN_KEY);
    }
  }, []);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Helper function to normalize attack path IDs by removing timestamp suffixes
  const normalizeAttackPathId = (id) => {
    if (!id) return '';
    // Remove the last timestamp part (after the last dash)
    const parts = id.split('-');
    if (parts.length > 1) {
      // Keep all parts except the last one if it looks like a timestamp (13+ digits)
      const lastPart = parts[parts.length - 1];
      if (lastPart.length >= 13 && /^\d+$/.test(lastPart)) {
        return parts.slice(0, -1).join('-');
      }
    }
    return id;
  };

  // Helper function to check if two attack path IDs match (with normalization)
  const attackPathIdsMatch = (id1, id2) => {
    if (!id1 || !id2) return false;
    if (id1 === id2) return true; // Exact match

    // Try normalized matching (remove timestamps)
    const normalized1 = normalizeAttackPathId(id1);
    const normalized2 = normalizeAttackPathId(id2);
    return normalized1 === normalized2 && normalized1.length > 0;
  };

  // Helper function to extract source risk and objectif from attack path ID
  const extractPathComponents = (attackPathId) => {
    if (!attackPathId) return { sourceRiskId: null, objectifId: null };

    const parts = attackPathId.split('-');
    if (parts.length >= 4) {
      // Format: source-{timestamp}-{sourceRiskId}-{dreadedEventId}-{stakeholderId}-{timestamp}
      return {
        sourceRiskId: parts.length > 2 ? parts[2] : null,
        dreadedEventId: parts.length > 3 ? parts[3] : null
      };
    }
    return { sourceRiskId: null, dreadedEventId: null };
  };

  // Helper function to get attack paths associated with a specific measure
  const getAssociatedAttackPaths = (measure) => {
    if (!measure) return [];

    let matchedPaths = [];

    // Method 1: Enhanced direct attackPaths array with improved ID matching from fully hydrated data
    if (measure.attackPaths && measure.attackPaths.length > 0 && attackPathsData.length > 0) {
      matchedPaths = attackPathsData.filter(path => {
        return measure.attackPaths.some(measurePathId =>
          attackPathIdsMatch(measurePathId, path.id)
        );
      });

      if (matchedPaths.length > 0) {
        return matchedPaths;
      }
    }

    // Method 2: Enhanced matching using attackPathId field (for AI-generated measures)
    if (measure.attackPathId && attackPathsData.length > 0) {
      matchedPaths = attackPathsData.filter(path =>
        attackPathIdsMatch(measure.attackPathId, path.id)
      );

      if (matchedPaths.length > 0) {
        return matchedPaths;
      }
    }

    // Fallback: Use attackPathsDetails directly from the measure (might be less hydrated)
    if (measure.attackPathsDetails && measure.attackPathsDetails.length > 0) {
      return measure.attackPathsDetails;
    }

    // No explicit associations found
    return [];
  };

  // Normalize a stakeholder entry (string id or { id, name } object) to { id, name }
  const normalizeStakeholderEntry = (entry) => {
    if (typeof entry === 'string') {
      return { id: entry, name: entry };
    }
    if (entry && typeof entry === 'object') {
      const id = entry.id != null ? String(entry.id) : '';
      const name = entry.name != null ? String(entry.name) : '';
      return { id: id || name, name: name || id };
    }
    return { id: '', name: '' };
  };

  // Check whether a stakeholder entry matches the active stakeholder filter (by id or name)
  const stakeholderEntryMatches = (entry, filterValue) => {
    if (!filterValue) return true;
    const normalizedFilter = String(filterValue).trim().toLowerCase();
    const { id, name } = normalizeStakeholderEntry(entry);
    return id.trim().toLowerCase() === normalizedFilter || name.trim().toLowerCase() === normalizedFilter;
  };

  // A measure matches the stakeholder filter when the filter matches a direct
  // measure.stakeholders entry or a stakeholder of an associated attack path
  const measureMatchesStakeholder = (measure, filterValue) => {
    if (!filterValue) return true;
    if (!measure) return false;
    const directMatch = (measure.stakeholders || []).some((entry) =>
      stakeholderEntryMatches(entry, filterValue)
    );
    if (directMatch) return true;
    return getAssociatedAttackPaths(measure).some((path) =>
      (path.stakeholders || []).some((entry) => stakeholderEntryMatches(entry, filterValue))
    );
  };

  // Union of stakeholders from hydrated attack paths + analysis stakeholder directory, deduped by id/name
  const stakeholderFilterOptions = useMemo(() => {
    const optionsByKey = new Map();
    const addEntry = (entry) => {
      const { id, name } = normalizeStakeholderEntry(entry);
      const key = (id || name).trim().toLowerCase();
      if (!key || optionsByKey.has(key)) return;
      optionsByKey.set(key, { id: id || name, name: name || id });
    };
    attackPathsData.forEach((path) => {
      (path.stakeholders || []).forEach(addEntry);
    });
    stakeholderDirectory.forEach(addEntry);
    return [...optionsByKey.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [attackPathsData, stakeholderDirectory]);

  // Unique responsible parties from measures for the responsible filter
  const responsibleOptions = useMemo(() => {
    const unique = new Set();
    measures.forEach((measure) => {
      const responsible = (measure.responsibleParty || '').toString().trim();
      if (responsible) unique.add(responsible);
    });
    return [...unique].sort((a, b) => a.localeCompare(b));
  }, [measures]);

  // Attack paths available for the attack-path filter
  const attackPathFilterOptions = useMemo(() => (
    attackPathsData.map((path) => ({
      id: path.id,
      label: `${path.strategicScenarioReference ? `${path.strategicScenarioReference} / ` : ''}${path.referenceCode || path.id}`
    }))
  ), [attackPathsData]);

  // Filtered measures (search + all dropdown filters)
  const filteredMeasures = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    return measures.filter((measure) => {
      if (term) {
        const haystack = [measure.title, measure.description, measure.responsibleParty, measure.notes]
          .map((value) => (value || '').toString().toLowerCase());
        if (!haystack.some((text) => text.includes(term))) return false;
      }
      if (filterCategory && measure.category !== filterCategory) return false;
      if (filterPriority && measure.priority !== filterPriority) return false;
      if (filterImplementation && measure.implementation !== filterImplementation) return false;
      if (filterResponsible && (measure.responsibleParty || '') !== filterResponsible) return false;
      if (filterAttackPath) {
        const attackPathIds = Array.isArray(measure.attackPaths) ? measure.attackPaths : [];
        const directMatch = attackPathIds.some((id) => attackPathIdsMatch(id, filterAttackPath))
          || (measure.attackPathId ? attackPathIdsMatch(measure.attackPathId, filterAttackPath) : false);
        if (!directMatch) return false;
      }
      if (!measureMatchesStakeholder(measure, filterStakeholder)) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measures, searchTerm, filterStakeholder, filterCategory, filterPriority, filterImplementation, filterResponsible, filterAttackPath, attackPathsData]);

  const hasActiveFilters = Boolean(
    (searchTerm || '').trim()
    || filterStakeholder
    || filterCategory
    || filterPriority
    || filterImplementation
    || filterResponsible
    || filterAttackPath
  );

  const clearFilters = () => {
    setSearchTerm('');
    setFilterStakeholder('');
    setFilterCategory('');
    setFilterPriority('');
    setFilterImplementation('');
    setFilterResponsible('');
    setFilterAttackPath('');
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />;
  };

  // Load attack paths data when component mounts
  useEffect(() => {
    const loadAttackPathsData = async () => {
      setDataLoading(true);
      try {
        const analysisId = localStorage.getItem('currentAnalysisId');
        if (analysisId) {
          console.log('🔄 Loading hydrated attack paths for measure table...');
          const response = await workshop3Service.getAttackPaths(analysisId);

          if (response.success && response.data) {
            const attackPaths = Array.isArray(response.data) ? response.data : [];
            console.log('✅ Loaded', attackPaths.length, 'hydrated attack paths');

            setAttackPathsData(attackPaths);

            // Créer un dictionnaire des sources de risque avec leurs objectifs visés
            const newSourceRisks = {};
            attackPaths.forEach(path => {
              if (path.sourceRiskId && path.sourceRiskName) {
                newSourceRisks[path.sourceRiskId] = {
                  name: path.sourceRiskName,
                  description: path.sourceRiskDescription || path.sourceRiskName,
                  objectifVise: path.objectifVise || '',
                  referenceCode: path.referenceCode || `CA${String(attackPaths.indexOf(path) + 1).padStart(2, '0')}`,
                  stakeholders: path.stakeholders || []
                };
              }
            });
            setSourceRisks(newSourceRisks);

            // Créer un dictionnaire des objectifs visés
            const newObjectifsVises = {};
            attackPaths.forEach(path => {
              if (path.objectifVise) {
                newObjectifsVises[path.objectifVise] = {
                  name: path.objectifVise,
                  description: path.objectifVise,
                  type: ''
                };
              }
            });
            setObjectifsVises(newObjectifsVises);
          }
        }
      } catch (error) {
        console.error('Error loading attack paths data:', error);
      } finally {
        setDataLoading(false);
      }
    };

    loadAttackPathsData();
  }, []);

  useEffect(() => {
    const loadStakeholderDirectory = async () => {
      try {
        const analysisId = localStorage.getItem('currentAnalysisId');
        if (!analysisId) {
          return;
        }
        const response = await api.get(`/analyses/${analysisId}/atelier3/stakeholders`);
        if (response && response.success && Array.isArray(response.data)) {
          setStakeholderDirectory(response.data);
        }
      } catch (error) {
        console.error('Error loading stakeholders for measure filters:', error);
      }
    };

    loadStakeholderDirectory();
  }, []);

  useEffect(() => {
    if (!measures.length || typeof window === 'undefined') {
      return;
    }

    const rawTarget = localStorage.getItem(WORKSHOP_MEASURE_NAVIGATION_KEY);
    if (!rawTarget) {
      return;
    }

    let target;
    try {
      target = JSON.parse(rawTarget);
    } catch (error) {
      console.warn('Failed to parse pending measure highlight payload:', error);
      localStorage.removeItem(WORKSHOP_MEASURE_NAVIGATION_KEY);
      return;
    }

    if (target?.tab !== 'atelier3-activite4' || target?.sourceType !== 'ecosystem') {
      return;
    }

    const matchedMeasure = measures.find((measure) => {
      const measureId = measure?.id != null ? String(measure.id) : '';
      const measureNames = [measure.title, measure.name]
        .map(normalizeMeasureText)
        .filter(Boolean);

      return (
        (target.measureId && measureId === String(target.measureId)) ||
        (target.controlId && measureId === String(target.controlId)) ||
        (target.normalizedMeasureName && measureNames.includes(target.normalizedMeasureName))
      );
    });

    if (!matchedMeasure) {
      return;
    }

    const timer = window.setTimeout(() => {
      const rowElement = Array.from(document.querySelectorAll('[data-measure-row="true"]')).find((element) => {
        const elementId = element.getAttribute('data-measure-id');
        const elementName = normalizeMeasureText(element.getAttribute('data-measure-name'));

        return (
          (matchedMeasure?.id != null && elementId === String(matchedMeasure.id)) ||
          (target.normalizedMeasureName && elementName === target.normalizedMeasureName)
        );
      });

      if (!rowElement) {
        return;
      }

      rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      flashHighlightElement(rowElement);
      localStorage.removeItem(WORKSHOP_MEASURE_NAVIGATION_KEY);
    }, 200);

    return () => window.clearTimeout(timer);
  }, [measures]);

  // Sort filtered measures
  const sortedMeasures = [...filteredMeasures].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    // Handle special cases
    if (sortField === 'category') {
      aValue = CATEGORIES[a.category]?.label || '';
      bValue = CATEGORIES[b.category]?.label || '';
    } else if (sortField === 'priority') {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      aValue = priorityOrder[a.priority] || 0;
      bValue = priorityOrder[b.priority] || 0;
    } else if (sortField === 'implementation') {
      const statusOrder = { implemented: 4, 'in-progress': 3, planned: 2, abandoned: 1 };
      aValue = statusOrder[a.implementation] || 0;
      bValue = statusOrder[b.implementation] || 0;
    }

    // Compare values
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-600">Chargement des mesures...</span>
      </div>
    );
  }

  const handleReturnToWorkshop5 = () => {
    if (typeof window === 'undefined') {
      return;
    }

    const restorePayload = {
      ...returnToWorkshop5,
      sourceTab: returnToWorkshop5?.sourceTab || 'atelier5-synthese-scenarios',
      viewMode: returnToWorkshop5?.viewMode || 'carousel',
      cardTab: returnToWorkshop5?.cardTab || 'measures',
      workshopViewMode: returnToWorkshop5?.workshopViewMode || 'detailed'
    };

    localStorage.removeItem(WORKSHOP_MEASURE_NAVIGATION_KEY);
    localStorage.removeItem(WORKSHOP_MEASURE_RETURN_KEY);
    localStorage.setItem(WORKSHOP5_RESTORE_CONTEXT_KEY, JSON.stringify(restorePayload));
    localStorage.setItem('ebiosrm_switch_to_tab', restorePayload.sourceTab);
    window.dispatchEvent(new CustomEvent('ebiosrm_switch_tab'));
    setReturnToWorkshop5(null);
  };

  const isReturnTargetMeasure = (measure) => {
    if (!returnToWorkshop5 || !measure) {
      return false;
    }

    const targetId = String(returnToWorkshop5.measureId || returnToWorkshop5.controlId || '');
    const measureId = String(measure.id || '');
    const targetName = normalizeMeasureText(returnToWorkshop5.measureName || returnToWorkshop5.normalizedMeasureName || '');
    const measureName = normalizeMeasureText(measure.title || measure.name || '');

    return (targetId && measureId === targetId) || (targetName && measureName === targetName);
  };

  if (measures.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
        <p className="text-gray-500">
          Aucune mesure de sécurité n'a encore été définie pour l'écosystème.
        </p>
        <p className="text-gray-500 mt-2">
          Utilisez les boutons ci-dessus pour ajouter des mesures manuellement ou générer des suggestions avec l'IA.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Filter bar — separate toolbar above the table card */}
      <div className="mb-3 px-4 py-3 bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="flex flex-wrap items-end gap-x-2 gap-y-2">
          <div className="flex flex-col gap-1">
            <label htmlFor="measure-filter-search-input" className="text-[11px] font-medium text-gray-500 leading-none">
              {t('workshop3.activity4.filters.searchLabel', 'Rechercher')}
            </label>
            <div className="relative w-48 max-w-full">
              <input
                id="measure-filter-search-input"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('workshop3.activity4.filters.searchPlaceholder', 'Rechercher une mesure...')}
                aria-label={t('workshop3.activity4.filters.searchLabel', 'Rechercher')}
                data-testid="measure-filter-search"
                className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 w-full text-xs shadow-sm"
              />
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="measure-filter-stakeholder-input" className="text-[11px] font-medium text-gray-500 leading-none">
              {t('workshop3.activity4.filters.stakeholder', 'Partie prenante')}
            </label>
            <select
              id="measure-filter-stakeholder-input"
              value={filterStakeholder}
              onChange={(e) => setFilterStakeholder(e.target.value)}
              aria-label={t('workshop3.activity4.filters.stakeholder', 'Partie prenante')}
              data-testid="measure-filter-stakeholder"
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-blue-500 focus:border-blue-500 shadow-sm bg-white w-auto max-w-[11rem] truncate"
            >
              <option value="">{t('workshop3.activity4.filters.all', 'Tous')}</option>
              {stakeholderFilterOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="measure-filter-category-input" className="text-[11px] font-medium text-gray-500 leading-none">
              {t('workshop3.activity4.filters.category', 'Catégorie')}
            </label>
            <select
              id="measure-filter-category-input"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              aria-label={t('workshop3.activity4.filters.category', 'Catégorie')}
              data-testid="measure-filter-category"
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-blue-500 focus:border-blue-500 shadow-sm bg-white w-auto max-w-[9rem] truncate"
            >
              <option value="">{t('workshop3.activity4.filters.all', 'Tous')}</option>
              {Object.keys(CATEGORIES).map((categoryId) => (
                <option key={categoryId} value={categoryId}>
                  {CATEGORIES[categoryId].label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="measure-filter-priority-input" className="text-[11px] font-medium text-gray-500 leading-none">
              {t('workshop3.activity4.filters.priority', 'Priorité')}
            </label>
            <select
              id="measure-filter-priority-input"
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              aria-label={t('workshop3.activity4.filters.priority', 'Priorité')}
              data-testid="measure-filter-priority"
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-blue-500 focus:border-blue-500 shadow-sm bg-white w-auto max-w-[8rem] truncate"
            >
              <option value="">{t('workshop3.activity4.filters.all', 'Tous')}</option>
              {Object.keys(PRIORITIES).map((priorityId) => (
                <option key={priorityId} value={priorityId}>
                  {PRIORITIES[priorityId].label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="measure-filter-implementation-input" className="text-[11px] font-medium text-gray-500 leading-none">
              {t('workshop3.activity4.filters.status', 'Statut')}
            </label>
            <select
              id="measure-filter-implementation-input"
              value={filterImplementation}
              onChange={(e) => setFilterImplementation(e.target.value)}
              aria-label={t('workshop3.activity4.filters.status', 'Statut')}
              data-testid="measure-filter-implementation"
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-blue-500 focus:border-blue-500 shadow-sm bg-white w-auto max-w-[9rem] truncate"
            >
              <option value="">{t('workshop3.activity4.filters.all', 'Tous')}</option>
              {Object.keys(IMPLEMENTATION_STATUSES).map((statusId) => (
                <option key={statusId} value={statusId}>
                  {IMPLEMENTATION_STATUSES[statusId].label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="measure-filter-responsible-input" className="text-[11px] font-medium text-gray-500 leading-none">
              {t('workshop3.activity4.filters.responsible', 'Responsable')}
            </label>
            <select
              id="measure-filter-responsible-input"
              value={filterResponsible}
              onChange={(e) => setFilterResponsible(e.target.value)}
              aria-label={t('workshop3.activity4.filters.responsible', 'Responsable')}
              data-testid="measure-filter-responsible"
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-blue-500 focus:border-blue-500 shadow-sm bg-white w-auto max-w-[9rem] truncate"
            >
              <option value="">{t('workshop3.activity4.filters.all', 'Tous')}</option>
              {responsibleOptions.map((responsible) => (
                <option key={responsible} value={responsible}>
                  {responsible}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="measure-filter-attack-path-input" className="text-[11px] font-medium text-gray-500 leading-none">
              {t('workshop3.activity4.filters.attackPath', "Chemin d'attaque")}
            </label>
            <select
              id="measure-filter-attack-path-input"
              value={filterAttackPath}
              onChange={(e) => setFilterAttackPath(e.target.value)}
              aria-label={t('workshop3.activity4.filters.attackPath', "Chemin d'attaque")}
              data-testid="measure-filter-attack-path"
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-blue-500 focus:border-blue-500 shadow-sm bg-white w-auto max-w-[10rem] truncate"
            >
              <option value="">{t('workshop3.activity4.filters.all', 'Tous')}</option>
              {attackPathFilterOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-medium text-transparent leading-none select-none" aria-hidden="true">
              &nbsp;
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={clearFilters}
                data-testid="measure-filters-clear"
                className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-150"
              >
                <X size={14} className="mr-1" />
                {t('workshop3.activity4.filters.clear', 'Effacer les filtres')}
              </button>
              <span data-testid="measure-filter-count" className="text-xs text-gray-500 whitespace-nowrap">
                {sortedMeasures.length} / {measures.length}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header with stats */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Mesures de sécurité</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {measures.length} mesure{measures.length > 1 ? 's' : ''} définie{measures.length > 1 ? 's' : ''}
                  {selectedMeasures.size > 0 && (
                    <span className="ml-2 text-blue-600 font-medium">
                      ({selectedMeasures.size} sélectionnée{selectedMeasures.size > 1 ? 's' : ''})
                    </span>
                  )}
                </p>
              </div>

              {/* Bulk delete button */}
              {selectedMeasures.size > 0 && (
                <button
                  onClick={handleBulkDeleteClick}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-lg hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-150"
                >
                  <Trash size={16} className="mr-2" />
                  Supprimer ({selectedMeasures.size})
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-4">

            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {measures.filter(m => m.implementation === 'implemented').length}
              </div>
              <div className="text-xs text-gray-500">{t('workshop4.operationalScenarios.measureForm.fields.implemented') || 'Implémentées'}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {measures.filter(m => m.implementation === 'in-progress').length}
              </div>
              <div className="text-xs text-gray-500">{t('workshop4.operationalScenarios.measureForm.fields.inProgress') || 'En cours'}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {measures.filter(m => m.implementation === 'planned').length}
              </div>
              <div className="text-xs text-gray-500">{t('workshop4.operationalScenarios.measureForm.fields.planned') || 'Planifiées'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="sr-only">Sélectionner tout</span>
                </div>
              </th>
              <th
                className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('title')}
              >
                <div className="flex items-center space-x-1">
                  <span>{t('workshop4.operationalScenarios.measureForm.fields.title') || 'Titre'}</span>
                  {getSortIcon('title')}
                </div>
              </th>
              <th
                className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('category')}
              >
                <div className="flex items-center space-x-1">
                  <span>{t('workshop4.operationalScenarios.measureForm.fields.category') || 'Catégorie'}</span>
                  {getSortIcon('category')}
                </div>
              </th>
              <th
                className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('priority')}
              >
                <div className="flex items-center space-x-1">
                  <span>{t('workshop4.operationalScenarios.measureForm.fields.priority') || 'Priorité'}</span>
                  {getSortIcon('priority')}
                </div>
              </th>
              <th
                className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('implementation')}
              >
                <div className="flex items-center space-x-1">
                  <span>{t('workshop4.operationalScenarios.measureForm.fields.implementationStatus') || 'Statut'}</span>
                  {getSortIcon('implementation')}
                </div>
              </th>
              <th
                className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('responsibleParty')}
              >
                <div className="flex items-center space-x-1">
                  <span>{t('workshop4.operationalScenarios.measureForm.fields.responsibleParty') || 'Responsable'}</span>
                  {getSortIcon('responsibleParty')}
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('workshop4.operationalScenarios.measureForm.sections.attackPaths') || "Chemins d'attaque"}
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                {t('common.actions') || 'Actions'}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {hasActiveFilters && sortedMeasures.length === 0 && (
              <tr>
                <td colSpan={9} className="px-6 py-10 text-center">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <Search size={20} className="text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-700">
                      {t('workshop3.activity4.filters.noResults', 'Aucune mesure ne correspond aux filtres actifs.')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('workshop3.activity4.filters.noResultsHint', 'Modifiez ou effacez les filtres pour afficher des mesures.')}
                    </p>
                    <button
                      type="button"
                      onClick={clearFilters}
                      data-testid="measure-filters-clear-empty"
                      className="mt-3 inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors duration-150"
                    >
                      {t('workshop3.activity4.filters.clear', 'Effacer les filtres')}
                    </button>
                  </div>
                </td>
              </tr>
            )}
            {sortedMeasures.map(measure => (
              <React.Fragment key={measure.id}>
                <tr
                  className="hover:bg-gray-50 transition-colors duration-150"
                  data-measure-row="true"
                  data-measure-id={measure.id != null ? String(measure.id) : ''}
                  data-measure-name={measure.title || measure.name || ''}
                >
                  <td className="px-6 py-5 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedMeasures.has(measure.id)}
                        onChange={() => handleSelectMeasure(measure.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <button
                        onClick={() => toggleExpand(measure.id)}
                        className="text-gray-400 hover:text-blue-600 hover:bg-blue-50 p-1 rounded-md transition-colors duration-150"
                      >
                        {expandedMeasure === measure.id ? (
                          <ChevronUp size={18} />
                        ) : (
                          <ChevronDown size={18} />
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="text-sm font-semibold text-gray-900 leading-tight">{measure.title}</div>
                        {isReturnTargetMeasure(measure) && (
                          <button
                            type="button"
                            onClick={handleReturnToWorkshop5}
                            className="inline-flex items-center px-2.5 py-1 text-xs font-semibold text-indigo-700 bg-indigo-100 border border-indigo-300 rounded-md hover:bg-indigo-200 transition-colors duration-150"
                            title="Retourner à l’Atelier 5"
                          >
                            <ArrowLeft size={14} className="mr-1.5" />
                            Retour Atelier 5
                          </button>
                        )}
                      </div>
                      {measure.description && (
                        <div className="text-xs text-gray-500 line-clamp-2">
                          {measure.description.length > 100
                            ? measure.description.substring(0, 100) + '...'
                            : measure.description
                          }
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium shadow-sm ${CATEGORIES[measure.category]?.color || CATEGORIES.other.color}`}>
                      {CATEGORIES[measure.category]?.label || measure.category}
                    </span>
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium shadow-sm ${PRIORITIES[measure.priority]?.color || 'bg-gray-100 text-gray-800'}`}>
                      {PRIORITIES[measure.priority]?.label || measure.priority}
                    </span>
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium shadow-sm ${IMPLEMENTATION_STATUSES[measure.implementation]?.color || 'bg-gray-100 text-gray-800'}`}>
                      {IMPLEMENTATION_STATUSES[measure.implementation]?.label || measure.implementation}
                    </span>
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm text-gray-900 font-medium">
                        {measure.responsibleParty || (
                          <span className="text-gray-400 italic text-xs">Non assigné</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex flex-wrap gap-2">
                      {(() => {
                        const associatedPaths = getAssociatedAttackPaths(measure);

                        if (associatedPaths.length > 0) {
                          return associatedPaths.map((path) => (
                            <div key={path.id} className="inline-flex items-center rounded overflow-hidden text-xs font-bold shadow-sm mr-1">
                              {path.strategicScenarioReference && (
                                <span className="px-1.5 py-0.5 bg-purple-100 text-purple-800 border-r border-purple-200">
                                  {path.strategicScenarioReference}
                                </span>
                              )}
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-800">
                                {path.referenceCode}
                              </span>
                            </div>
                          ));
                        } else {
                          return (
                            <span className="text-xs text-gray-500 italic">
                              Aucun chemin associé
                            </span>
                          );
                        }
                      })()}
                    </div>
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => onEdit(measure)}
                        className="inline-flex items-center p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors duration-150"
                        title="Modifier"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => {
                          setDeleteModal({
                            isOpen: true,
                            itemType: 'ecosystemMeasure',
                            itemName: measure.title,
                            itemId: measure.id,
                            isBulk: false
                          });
                        }}
                        className="inline-flex items-center p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors duration-150"
                        title="Supprimer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>

                {/* Expanded details */}
                {expandedMeasure === measure.id && (
                  <tr>
                    <td colSpan={9} className="px-6 py-6 bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-200">
                      <div className="max-w-6xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Left Column */}
                          <div className="space-y-4">
                            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                              <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                                <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                                Description
                              </h4>
                              <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{measure.description}</p>
                            </div>

                            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                                Détails de mise en œuvre
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {measure.responsibleParty && (
                                  <div className="bg-gray-50 rounded-lg p-3">
                                    <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Responsable</h5>
                                    <p className="text-sm text-gray-900">{measure.responsibleParty}</p>
                                  </div>
                                )}

                                {measure.deadline && (
                                  <div className="bg-gray-50 rounded-lg p-3">
                                    <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Échéance</h5>
                                    <p className="text-sm text-gray-900">{new Date(measure.deadline).toLocaleDateString()}</p>
                                  </div>
                                )}

                                {/* Efficacité Détaillée */}
                                <div className="bg-gray-50 rounded-lg p-3 md:col-span-2">
                                  <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Efficacité de la mesure</h5>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    <div className="flex flex-col">
                                      <span className="text-[10px] font-semibold text-gray-500">DÉPENDANCE (D)</span>
                                      <span className="text-sm font-bold text-blue-600">
                                        -{measure.dependanceEffectiveness || 0}
                                      </span>
                                    </div>
                                    <div className="flex flex-col">
                                      <span className="text-[10px] font-semibold text-gray-500">PÉNÉTRATION (P)</span>
                                      <span className="text-sm font-bold text-blue-600">
                                        -{measure.penetrationEffectiveness || 0}
                                      </span>
                                    </div>
                                    <div className="flex flex-col">
                                      <span className="text-[10px] font-semibold text-gray-500">MATURITÉ (M)</span>
                                      <span className="text-sm font-bold text-green-600">
                                        +{measure.maturiteCyberEffectiveness || 0}
                                      </span>
                                    </div>
                                    <div className="flex flex-col">
                                      <span className="text-[10px] font-semibold text-gray-500">CONFIANCE (C)</span>
                                      <span className="text-sm font-bold text-green-600">
                                        +{measure.confianceEffectiveness || 0}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Right Column */}
                          <div className="space-y-4">
                            {/* Chemins d'attaque traités */}
                            {(() => {
                              const associatedPaths = getAssociatedAttackPaths(measure);


                              if (associatedPaths.length > 0) {
                                return (
                                  <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
                                    <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                                      <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1.5"></div>
                                      Chemins d'attaque traités
                                    </h4>
                                    <div className="space-y-2">
                                      {associatedPaths.map((path) => (
                                        <div key={path.id} className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                                          {/* Header with references */}
                                          <div className="flex items-center gap-2 mb-1.5">
                                            <div className="flex items-center text-xs font-bold rounded overflow-hidden border border-purple-200 shadow-sm">
                                              {path.strategicScenarioReference && (
                                                <span className="px-1.5 py-0.5 bg-purple-100 text-purple-800 border-r border-purple-200">
                                                  {path.strategicScenarioReference}
                                                </span>
                                              )}
                                              <span className="px-2 py-0.5 bg-blue-100 text-blue-800">
                                                {path.referenceCode}
                                              </span>
                                            </div>
                                            {path.type === 'direct' && (
                                              <span className="text-xs text-gray-600">Direct</span>
                                            )}
                                          </div>

                                          {/* Compact info grid */}
                                          <div className="grid grid-cols-2 gap-1.5 text-xs">
                                            <div className="flex items-start gap-1">
                                              <span className="text-gray-500 font-medium flex-shrink-0">Source:</span>
                                              <span className="text-red-700">{path.sourceRiskName}</span>
                                            </div>
                                            <div className="flex items-start gap-1">
                                              <span className="text-gray-500 font-medium flex-shrink-0">Objectif:</span>
                                              <span className="text-green-700">{path.objectifVise}</span>
                                            </div>
                                            {path.businessValueName && (
                                              <div className="col-span-2 flex items-start gap-1">
                                                <span className="text-gray-500 font-medium flex-shrink-0">VM:</span>
                                                <span className="text-blue-700">{path.businessValueName}</span>
                                              </div>
                                            )}
                                            {path.supportingAssets && path.supportingAssets.length > 0 && (
                                              <div className="col-span-2 flex items-start gap-1">
                                                <span className="text-gray-500 font-medium flex-shrink-0">BS:</span>
                                                <div className="flex flex-wrap gap-1">
                                                  {path.supportingAssets.map((asset, idx) => (
                                                    <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase font-medium bg-orange-50 text-orange-800 border border-orange-200">
                                                      {asset.name || asset}
                                                    </span>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                            {path.stakeholders && path.stakeholders.length > 0 && (
                                              <div className="col-span-2 flex items-start gap-1">
                                                <span className="text-gray-500 font-medium flex-shrink-0">PP:</span>
                                                <div className="flex flex-wrap gap-1">
                                                  {path.stakeholders.map((stakeholder, idx) => (
                                                    <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800 border border-yellow-200">
                                                      {typeof stakeholder === 'object' ? stakeholder.name || 'Partie Prenante' : stakeholder}
                                                    </span>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    {dataLoading && measure.id === expandedMeasure && (
                                      <p className="text-xs text-gray-500 mt-2 text-center">Chargement des données...</p>
                                    )}
                                  </div>
                                );
                              }
                              return null;
                            })()}

                            {measure.notes && (
                              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                                  <div className="w-2 h-2 bg-gray-500 rounded-full mr-2"></div>
                                  Notes
                                </h4>
                                <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{measure.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ ...deleteModal, isOpen: false })}
        onConfirm={confirmDelete}
        itemName={deleteModal.itemName}
        itemType={deleteModal.itemType}
      />
      </div>
    </>
  );
};

export default MeasureTable;
